import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  MessageCircle,
  X,
  Minimize2,
  Maximize2,
  AlertTriangle,
  Send,
} from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { ragAPI } from "../../../services/api";
import ReactMarkdown from "react-markdown";

const clamp = (s) => (s ?? "").toString();

/**
 * Small helper to format backend errors into something readable.
 */
const toErrorMessage = (e) => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || "").join(" | ");
  }
  if (typeof detail === "string") return detail;
  return e?.message || "Request failed.";
};

const normalizeAnswerText = (text) => {
  const t = clamp(text || "");

  // Unescape \* -> *
  const unescaped = t.replace(/\\\*/g, "*");

  // Keep markdown bold (**...**) and let ReactMarkdown render it.
  return unescaped;
};

/**
 * Project-level RAG chat widget.
 * - Shows floating button bottom-right
 * - Click → opens chat panel
 * - Can be maximized to full screen
 */
const ProjectRagChatWidget = () => {
  const { activeProject } = useProjectContext();

  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]); // { id, role, text, is_confident?, used_chunks? }

  // typing effect state
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [typedText, setTypedText] = useState("");

  // "AI is thinking..." animated dots state
  const [thinkingDots, setThinkingDots] = useState("");

  const projectId = activeProject?.id;
  const projectName = activeProject?.name || "Project";

  const canSend = useMemo(() => {
    return clamp(input).trim().length > 0 && !sending;
  }, [input, sending]);

  const isThinking = useMemo(() => sending, [sending]);

  const handleSend = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!canSend || !projectId) return;

      const question = clamp(input).trim();
      setInput("");

      const userMessage = {
        id: `u-${Date.now()}`,
        role: "user",
        text: question,
      };

      setMessages((prev) => [...prev, userMessage]);
      setSending(true);

      try {
        const res = await ragAPI.askQuestion(projectId, question);
        const data = res?.data || {};

        const assistantMessage = {
          id: `a-${Date.now()}`,
          role: "assistant",
          text: clamp(data.answer || "I couldn't generate an answer."),
          is_confident: !!data.is_confident,
          used_chunks: Array.isArray(data.used_chunks) ? data.used_chunks : [],
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // start typing effect for this assistant message
        setTypingMessageId(assistantMessage.id);
        setTypedText("");
      } catch (err) {
        const assistantMessage = {
          id: `a-${Date.now()}`,
          role: "assistant",
          text: `RAG Assistant error: ${toErrorMessage(err)}`,
          is_confident: false,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // no typing effect for error
        setTypingMessageId(null);
        setTypedText("");
      } finally {
        setSending(false);
      }
    },
    [canSend, input, projectId],
  );

  /**
   * Typewriter effect for the current assistant message (typingMessageId).
   */
  useEffect(() => {
    if (!typingMessageId) return;

    const message = messages.find((m) => m.id === typingMessageId);
    if (!message || message.role !== "assistant") return;

    const fullText = message.text || "";
    if (!fullText.length) return;

    let index = 0;
    setTypedText("");

    const TYPING_SPEED_MS = 10;

    const intervalId = setInterval(() => {
      index += 1;
      setTypedText(fullText.slice(0, index));

      if (index >= fullText.length) {
        clearInterval(intervalId);
        setTypingMessageId(null); // done
      }
    }, TYPING_SPEED_MS);

    return () => clearInterval(intervalId);
  }, [typingMessageId, messages]);

  /**
   * Animated "AI is thinking..." dots while the model is processing / typing.
   */
  useEffect(() => {
    if (!isThinking) {
      setThinkingDots("");
      return;
    }

    const dots = ["", ".", "..", "..."];
    let i = 0;

    const interval = setInterval(() => {
      setThinkingDots(dots[i % dots.length]);
      i += 1;
    }, 500);

    return () => clearInterval(interval);
  }, [isThinking]);

  if (!projectId) {
    return null;
  }

  const handleToggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMaximized(false);
  };

  const handleMaximizeToggle = () => {
    setIsMaximized((prev) => !prev);
  };

  // Layout classes for minimized vs maximized
  const containerClasses = isMaximized
    ? "fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2"
    : "fixed bottom-20 right-4 z-40";

  const panelClasses = isMaximized
    ? "w-full max-w-[960px] h-[90vh] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
    : "w-[360px] max-w-[calc(100vw-2rem)] h-[420px] bg-white rounded-lg shadow-xl flex flex-col border border-gray-200";

  return (
    <>
      {/* Floating bubble button (always visible when project open) */}
      <button
        type="button"
        onClick={handleToggleOpen}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center justify-center rounded-full shadow-lg bg-orange-500 hover:bg-orange-600 text-white w-12 h-12 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
        title="SmokyQ AI Assistant"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className={containerClasses}>
          <div className={panelClasses}>
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-gray-700">
                  SmokyQ AI Assistant
                </span>
                <span className="text-[11px] text-gray-500 truncate max-w-[260px]">
                  Project: {projectName}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleMaximizeToggle}
                  className="p-1 rounded hover:bg-gray-100 text-gray-500"
                  title={isMaximized ? "Restore" : "Maximize"}
                >
                  {isMaximized ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1 rounded hover:bg-gray-100 text-gray-500"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 py-2 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-xs text-gray-500 h-full flex items-center justify-center text-center px-4">
                  Ask questions about this project’s specifications and
                  documents. Answers are generated from the RAG index for this
                  project only.
                </div>
              ) : (
                <div className="space-y-2">
                  {messages.map((m) => {
                    const isTyping = m.id === typingMessageId;

                    // Raw text from either the full message or the typing buffer
                    const rawText =
                      m.role === "assistant" && isTyping ? typedText : m.text;

                    const displayText = normalizeAnswerText(rawText);

                    return (
                      <div
                        key={m.id}
                        className={
                          m.role === "user"
                            ? "flex justify-end"
                            : "flex justify-start"
                        }
                      >
                        <div
                          className={
                            m.role === "user"
                              ? "max-w-[80%] bg-orange-500 text-white text-xs px-3 py-2 rounded-2xl rounded-br-sm shadow"
                              : "max-w-[80%] bg-white text-gray-800 text-xs px-3 py-2 rounded-2xl rounded-bl-sm shadow border border-gray-200"
                          }
                        >
                          <div className="whitespace-pre-wrap break-words prose prose-sm max-w-none">
                            <ReactMarkdown>{displayText}</ReactMarkdown>
                          </div>

                          {/* Confidence warning for assistant answers */}
                          {m.role === "assistant" &&
                            m.is_confident === false && (
                              <div className="mt-2 flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1">
                                <AlertTriangle className="w-3 h-3 mt-[1px]" />
                                <span>
                                  Answer may be incomplete; context was not
                                  fully sufficient.
                                </span>
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Thinking indicator under messages */}
                  {isThinking && (
                    <div className="mt-1 text-[11px] text-gray-500 italic flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                      <span>SmokyQ is thinking{thinkingDots}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={handleSend}
              className="border-t border-gray-200 px-3 py-2 bg-white rounded-b-lg"
            >
              <div className="flex items-end gap-2">
                <textarea
                  rows={1}
                  className="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Ask a question about the project specs..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={!canSend}
                  className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-xs font-medium ${
                    canSend
                      ? "bg-orange-500 text-white hover:bg-orange-600"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {sending ? (
                    // You can keep this or remove since we now show the global "AI is thinking..."
                    <span className="animate-pulse">...</span>
                  ) : (
                    <>
                      <Send className="w-3 h-3 mr-1" />
                      Ask
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectRagChatWidget;
